import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import {
  RotateCcw, Clock, CheckCircle, XCircle, AlertCircle, Plus,
  Package, Calendar, Eye, Search, X
} from 'lucide-react';
import '../styles/refund-request.css';

const STATUS_TABS = [
  { key: 'all',        label: 'All' },
  { key: 'pending',    label: 'Pending' },
  { key: 'approved',   label: 'Approved' },
  { key: 'processing', label: 'Processing' },
  { key: 'rejected',   label: 'Rejected' }
];

const REASON_OPTIONS = [
  { value: 'defective',        label: 'Product is defective' },
  { value: 'wrong_item',       label: 'Wrong item received' },
  { value: 'not_as_described', label: 'Item not as described' },
  { value: 'changed_mind',     label: 'Changed my mind' },
  { value: 'duplicate',        label: 'Duplicate order' },
  { value: 'other',            label: 'Other' }
];

const reasonLabel = (value) => {
  if (!value) return '—';
  return REASON_OPTIONS.find(o => o.value === value)?.label
    || value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ');
};

const pillClass = (status) => `crefund-pill crefund-pill--${(status || 'pending').toLowerCase()}`;

const formatCurrency = (amount) => new Intl.NumberFormat('en-NG', {
  style: 'currency', currency: 'NGN', minimumFractionDigits: 2, maximumFractionDigits: 2
}).format(parseFloat(amount) || 0);

const formatDate = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

const StatusIcon = ({ status, size = 12 }) => {
  switch (status) {
    case 'approved':   return <CheckCircle size={size} aria-hidden />;
    case 'rejected':   return <XCircle size={size} aria-hidden />;
    case 'processing': return <AlertCircle size={size} aria-hidden />;
    default:           return <Clock size={size} aria-hidden />;
  }
};

const RefundRequest = () => {
  const navigate = useNavigate();
  const { customer, isAuthenticated, loading: authLoading } = useCustomerAuth();
  const [refundRequests, setRefundRequests] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRequest, setViewingRequest] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [refundData, setRefundData] = useState({ refundAmount: '', reason: '', customerNotes: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/login');
  }, [isAuthenticated, authLoading, navigate]);

  const loadRefundRequests = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/refund-requests/customer/my-requests', { credentials: 'include' });
      const data = await response.json();
      if (data.success) {
        setRefundRequests(data.refunds || []);
      } else {
        setError('Failed to load refund requests');
      }
    } catch (err) {
      setError('Failed to load refund requests');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadOrders = useCallback(async () => {
    try {
      const response = await fetch('/api/customer/order-tracking', { credentials: 'include' });
      const data = await response.json();
      if (data.success || data.orders) {
        const eligibleOrders = (data.orders || []).filter(order =>
          order.status !== 'shipped' && order.status !== 'delivered'
        );
        setOrders(eligibleOrders);
      }
    } catch (err) {
      /* non-blocking */
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && customer) {
      loadRefundRequests();
      loadOrders();
    }
  }, [isAuthenticated, customer, loadRefundRequests, loadOrders]);

  const createRefundRequest = async () => {
    if (!selectedOrder || !refundData.refundAmount || !refundData.reason) {
      setError('Please fill in all required fields');
      return;
    }
    try {
      setSubmitting(true);
      setError('');
      const response = await fetch('/api/refund-requests/customer/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          orderId:       selectedOrder.id,
          refundReason:  refundData.reason,
          customerNotes: refundData.customerNotes
        })
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('Refund request submitted');
        setTimeout(() => setSuccess(''), 3000);
        setShowCreateModal(false);
        setRefundData({ refundAmount: '', reason: '', customerNotes: '' });
        setSelectedOrder(null);
        loadRefundRequests();
      } else {
        setError(data.error || 'Failed to submit refund request');
      }
    } catch (err) {
      setError('Failed to submit refund request');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredRequests = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    return refundRequests.filter(request => {
      const orderNum = (request.orderId?.orderNumber || '').toLowerCase();
      const reason = (request.refundReason || '').toLowerCase();
      const matchesSearch = !term || orderNum.includes(term) || reason.includes(term);
      const matchesStatus = statusFilter === 'all' || request.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [refundRequests, searchTerm, statusFilter]);

  const counts = useMemo(() => ({
    total:      refundRequests.length,
    pending:    refundRequests.filter(r => r.status === 'pending').length,
    approved:   refundRequests.filter(r => r.status === 'approved').length,
    rejected:   refundRequests.filter(r => r.status === 'rejected').length
  }), [refundRequests]);

  if (authLoading) {
    return (
      <div className="crefund">
        <div className="crefund__skel crefund__skel--head" />
        <div className="crefund__skel crefund__skel--block" />
      </div>
    );
  }
  if (!isAuthenticated) return null;

  if (loading) {
    return (
      <div className="crefund">
        <div className="crefund__skel crefund__skel--head" />
        <div className="crefund__skel crefund__skel--block" />
        <div className="crefund__skel crefund__skel--block" />
      </div>
    );
  }

  return (
    <div className="crefund">
      <header className="crefund__head">
        <div className="crefund__head-text">
          <h1 className="crefund__title">Refunds</h1>
          <p className="crefund__sub">
            {counts.total > 0
              ? `${counts.total} request${counts.total === 1 ? '' : 's'} · ${counts.pending} pending · ${counts.approved} approved · ${counts.rejected} rejected`
              : 'Track your refund requests here.'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreateModal(true)}
          className="crefund__btn crefund__btn--primary"
        >
          <Plus size={14} aria-hidden /> New request
        </button>
      </header>

      {error && (
        <div className="crefund__alert crefund__alert--error" role="alert">
          <XCircle size={14} aria-hidden /> <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="crefund__alert crefund__alert--success" role="status">
          <CheckCircle size={14} aria-hidden /> <span>{success}</span>
        </div>
      )}

      <div className="crefund__controls">
        <div className="crefund__search">
          <Search size={14} className="crefund__search-icon" aria-hidden />
          <input
            type="text"
            placeholder="Search by order or reason"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="crefund__search-input"
          />
        </div>
        <nav className="crefund__tabs" aria-label="Filter by status">
          {STATUS_TABS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => setStatusFilter(key)}
              className={`crefund-tab ${statusFilter === key ? 'is-active' : ''}`}
              aria-pressed={statusFilter === key}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>

      {filteredRequests.length === 0 ? (
        <div className="crefund__empty">
          <RotateCcw size={32} aria-hidden />
          <div>
            <div className="crefund__empty-title">
              {counts.total === 0 ? 'No refund requests yet' : 'No requests match your filters'}
            </div>
            <div className="crefund__empty-sub">
              {counts.total === 0
                ? 'Submit your first request to track its status here.'
                : 'Try clearing the search or status filter.'}
            </div>
          </div>
          {counts.total === 0 && (
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              className="crefund__btn crefund__btn--primary"
            >
              <Plus size={14} aria-hidden /> New request
            </button>
          )}
        </div>
      ) : (
        <ul className="crefund__list">
          {filteredRequests.map((request) => (
            <li key={request.id || request._id} className="crefund-row">
              <div className="crefund-row__body">
                <div className="crefund-row__head">
                  <div className="crefund-row__order">
                    Order #{request.orderId?.orderNumber || 'N/A'}
                  </div>
                  <span className={pillClass(request.status)}>
                    <StatusIcon status={request.status} />
                    {request.status}
                  </span>
                </div>
                <dl className="crefund-row__meta">
                  <div>
                    <dt>Amount</dt>
                    <dd>{formatCurrency(request.refundAmount)}</dd>
                  </div>
                  <div>
                    <dt><Calendar size={11} aria-hidden /> Requested</dt>
                    <dd>{formatDate(request.createdAt)}</dd>
                  </div>
                  <div>
                    <dt><Package size={11} aria-hidden /> Reason</dt>
                    <dd>{reasonLabel(request.refundReason)}</dd>
                  </div>
                </dl>
                {request.customerNotes && (
                  <p className="crefund-row__note">{request.customerNotes}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => { setViewingRequest(request); setShowViewModal(true); }}
                className="crefund-row__view"
                aria-label="View details"
              >
                <Eye size={14} aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Create modal */}
      {showCreateModal && (
        <div className="crefund-modal" role="dialog" aria-modal="true" onMouseDown={() => setShowCreateModal(false)}>
          <div className="crefund-modal__panel" onMouseDown={(e) => e.stopPropagation()}>
            <header className="crefund-modal__head">
              <h2 className="crefund-modal__title">
                <RotateCcw size={14} aria-hidden /> New refund request
              </h2>
              <button type="button" onClick={() => setShowCreateModal(false)} className="crefund-modal__close" aria-label="Close">
                <X size={16} aria-hidden />
              </button>
            </header>
            <div className="crefund-modal__body">
              <label className="crefund__field">
                <span className="crefund__label">Order *</span>
                <select
                  value={selectedOrder?.id || ''}
                  onChange={(e) => {
                    const order = orders.find(o => o.id === e.target.value);
                    setSelectedOrder(order);
                    if (order) {
                      setRefundData(prev => ({ ...prev, refundAmount: String(order.total) }));
                    }
                  }}
                  className="crefund__input"
                >
                  <option value="">Choose an order…</option>
                  {orders.map(order => (
                    <option key={order.id} value={order.id}>
                      #{order.orderNumber || (order.id ? order.id.slice(-8).toUpperCase() : 'N/A')} · {formatCurrency(order.total)} · {order.status}
                    </option>
                  ))}
                </select>
                {orders.length === 0 && (
                  <span className="crefund__hint">No eligible orders. Only pending/processing orders can be refunded.</span>
                )}
              </label>

              <label className="crefund__field">
                <span className="crefund__label">Refund amount (NGN) *</span>
                <input
                  type="number"
                  value={refundData.refundAmount}
                  onChange={(e) => setRefundData(prev => ({ ...prev, refundAmount: e.target.value }))}
                  placeholder="0.00"
                  max={selectedOrder?.total || ''}
                  className="crefund__input"
                />
                {selectedOrder && (
                  <span className="crefund__hint">Order total: {formatCurrency(selectedOrder.total)}</span>
                )}
              </label>

              <label className="crefund__field">
                <span className="crefund__label">Reason *</span>
                <select
                  value={refundData.reason}
                  onChange={(e) => setRefundData(prev => ({ ...prev, reason: e.target.value }))}
                  className="crefund__input"
                >
                  <option value="">Select a reason…</option>
                  {REASON_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </label>

              <label className="crefund__field">
                <span className="crefund__label">Additional notes <span className="crefund__opt">(optional)</span></span>
                <textarea
                  value={refundData.customerNotes}
                  onChange={(e) => setRefundData(prev => ({ ...prev, customerNotes: e.target.value }))}
                  placeholder="Anything else the merchant should know?"
                  rows={3}
                  className="crefund__input"
                />
              </label>
            </div>
            <footer className="crefund-modal__foot">
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="crefund__btn crefund__btn--ghost"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={createRefundRequest}
                disabled={submitting || !selectedOrder || !refundData.refundAmount || !refundData.reason}
                className="crefund__btn crefund__btn--primary"
              >
                {submitting ? 'Submitting…' : 'Submit request'}
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* View modal */}
      {showViewModal && viewingRequest && (
        <div className="crefund-modal" role="dialog" aria-modal="true" onMouseDown={() => setShowViewModal(false)}>
          <div className="crefund-modal__panel" onMouseDown={(e) => e.stopPropagation()}>
            <header className="crefund-modal__head">
              <h2 className="crefund-modal__title">
                Order #{viewingRequest.orderId?.orderNumber || 'N/A'}
              </h2>
              <button type="button" onClick={() => setShowViewModal(false)} className="crefund-modal__close" aria-label="Close">
                <X size={16} aria-hidden />
              </button>
            </header>
            <div className="crefund-modal__body">
              <dl className="crefund-view">
                <div className="crefund-view__row">
                  <dt>Status</dt>
                  <dd>
                    <span className={pillClass(viewingRequest.status)}>
                      <StatusIcon status={viewingRequest.status} />
                      {viewingRequest.status}
                    </span>
                  </dd>
                </div>
                <div className="crefund-view__row">
                  <dt>Refund amount</dt>
                  <dd>{formatCurrency(viewingRequest.refundAmount)}</dd>
                </div>
                <div className="crefund-view__row">
                  <dt>Requested</dt>
                  <dd>{formatDate(viewingRequest.createdAt)}</dd>
                </div>
                <div className="crefund-view__row">
                  <dt>Reason</dt>
                  <dd>{reasonLabel(viewingRequest.refundReason)}</dd>
                </div>
              </dl>

              {viewingRequest.customerNotes && (
                <section className="crefund-view__section">
                  <div className="crefund-view__label">Your notes</div>
                  <p className="crefund-view__text">{viewingRequest.customerNotes}</p>
                </section>
              )}
              {viewingRequest.merchantResponse?.notes && (
                <section className="crefund-view__section">
                  <div className="crefund-view__label">Merchant response</div>
                  <p className="crefund-view__text crefund-view__text--merchant">
                    {viewingRequest.merchantResponse.notes}
                  </p>
                </section>
              )}
            </div>
            <footer className="crefund-modal__foot">
              <button
                type="button"
                onClick={() => setShowViewModal(false)}
                className="crefund__btn crefund__btn--ghost"
              >
                Close
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};

export default RefundRequest;
